// packages/ui/src/session/closeController.ts
import type { PlatformBridge, ToolHost } from '@slate/core';
import { evaluateCloseGuard } from '../components/closeGuard';
import { terminalSessionRegistry } from '../tools/terminal/sessionRegistry';

export interface BusyTerminalInfo {
  id: string;
  title: string;
  detail?: string;
}

/**
 * Pure decision: with crash-safe drafts covering file content, the ONLY thing
 * that blocks a close/quit is a terminal with a running process (a quit would
 * SIGHUP it). Dirty docs never block.
 */
export function decideClose(input: {
  busyTerminals: BusyTerminalInfo[];
  dirtyCount: number;
}): { blocked: boolean; busyTerminals: BusyTerminalInfo[] } {
  return { blocked: input.busyTerminals.length > 0, busyTerminals: input.busyTerminals };
}

/**
 * Flush callbacks registered by the persistence hooks. attemptClose awaits all
 * of them so the last edits and the manifest hit disk before we exit.
 */
type FlushFn = () => Promise<void> | void;
const flushers = new Set<FlushFn>();
export function registerFlusher(fn: FlushFn): () => void {
  flushers.add(fn);
  return () => flushers.delete(fn);
}
export async function flushAll(): Promise<void> {
  await Promise.all(
    Array.from(flushers).map(async (f) => {
      // A flusher can fail synchronously (throw) or asynchronously (reject).
      // Wrapping the call in an async fn funnels a sync throw into this catch
      // too, so one failing flusher never aborts the quit flush of the others.
      try {
        await f();
      } catch {
        /* swallow: best-effort flush, never block the close path */
      }
    }),
  );
}

export interface AttemptCloseDeps {
  platform: PlatformBridge;
  toolHost: ToolHost;
  /** Show the busy-terminal confirm dialog; resolves true to proceed, false to cancel. */
  confirmBusy: (busy: BusyTerminalInfo[]) => Promise<boolean>;
}

/**
 * The single funnel every close/quit path calls. Flushes drafts + manifest,
 * checks for busy terminals, optionally confirms, kills terminals, and exits
 * via exitApp (whole app) or closeWindow (single window).
 */
export async function attemptClose(
  mode: 'app' | 'window',
  deps: AttemptCloseDeps,
): Promise<void> {
  await flushAll();
  const { busyReasons } = await evaluateCloseGuard(deps.toolHost);
  const busyTerminals: BusyTerminalInfo[] = busyReasons.map((r) => ({
    id: r.instanceId ?? r.toolId,
    title: r.title,
    detail: r.detail,
  }));
  const { blocked } = decideClose({ busyTerminals, dirtyCount: 0 });
  if (blocked) {
    const proceed = await deps.confirmBusy(busyTerminals);
    if (!proceed) return;
  }
  await terminalSessionRegistry.killAll();
  if (mode === 'app') await deps.platform.exitApp?.();
  else await deps.platform.closeWindow?.();
}
